import Form from "../../../components/form/form";
import MenuTop from "../../../components/menu/top/menu-top";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import InfoComponent from "../../../components/info/info";
import financialFormData from "../../../features/financial/forms/financial-form-data";
import storage from "../../../shared/services/storage";
import { useCallback, useEffect, useState } from "react";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { 
    fetchFinancialData, 
    fetchFinancialsByProjectIdService, 
    setFinancialData,
    deleteFinancialService 
} from "../../../features/financial/service/financeiro-service";
import { User } from "../../../shared/interfaces/user";
import { fetchRubricasGrouped } from "../../../features/rubrica/service/rubrica";
import { fetchTiposLancamento } from "../../../features/lancamento/service/tipo-lancamento";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../../components/alert/alert";
import { FormProps } from "../../../shared/interfaces/form";
import { CustomModal, useModal } from "../../../components/modals/common/index";
import { Typography } from "@mui/material";
import './style.css';
import formatDateToISO from "../../../shared/utils/data-formatter";
import { fetchBeneficiariesByIdCompanyService } from "../../../features/beneficiary/service/beneficiary-service";
import { toCents } from "../../../shared/utils/money";

function FinancialEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [formData, setFormData] = useState<FormProps[]>([]);
    const [originalFinancial, setOriginalFinancial] = useState<any>(null);
    const [projectId, setProjectId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    
    // Hook para controlar o estado da modal de confirmação de exclusão
    const deleteModal = useModal();
    
    const getTiposLancamento = useCallback(async () => {
        try {
            const lancamentos = await fetchTiposLancamento();
            return lancamentos?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error(error);
        }
    }, []);
    
    const getRubricasGrouped = useCallback(async () => {
            try {
                return await fetchRubricasGrouped();
            } catch (error) {
                console.error(error);
            }
        }, []);
    
    const getProjectsByFinancier = useCallback(async () => {
        const userData: User = await JSON.parse(storage.getStorage('user') || '');
        
        try {
            const projects = await fetchFinancialsByProjectIdService(userData.company.id as string);
            return projects?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getBeneficiaries = useCallback(async (companyId: string) => {
            try {
                return await fetchBeneficiariesByIdCompanyService(companyId);
            } catch (error) {
                console.error(error);
            }
    }, []);
    
    const fetchFinancialEditData = useCallback(async (financialId: string) => {
        setIsLoading(true);

        try {
            const financial = await fetchFinancialData(financialId);
            const { idProject, project } = financial[0]

            if (!financial || financial.length === 0) {
                showAlert('Dados financeiros não encontrados', SEVERITY.ERROR);
                return;
            }
            
            setProjectId(idProject);
            
            const [valueLancamentos, valueRubricas, valueProjects, valueBeneficiaries] = await Promise.all([
                getTiposLancamento(),
                getRubricasGrouped(),
                getProjectsByFinancier(),
                getBeneficiaries(project.companyId)
            ]);

            setOriginalFinancial(financial[0]);
            setFormData(financialFormData(financialId, financial[0], valueLancamentos, valueRubricas, valueProjects, valueBeneficiaries, false));
        } catch (error) {
            setIsLoading(false);
            showAlert('Erro ao carregar dados financeiros', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }

    }, [getTiposLancamento, getRubricasGrouped, getProjectsByFinancier, getBeneficiaries, showAlert, id]);
    
    useEffect(() => {
        if (id) {
            fetchFinancialEditData(id as string);
        }
    }, [fetchFinancialEditData, id]);
    
    const onSubmitForm = (data: any) => {
        setIsLoading(true);

        if (!data) {
            showAlert('Dados inválidos', SEVERITY.ERROR);
            return;
        }
        
        if (!id) {
            showAlert('ID não fornecido para edição', SEVERITY.ERROR);
            return;
        }

        const financialDataAddedFormatted = formatDateToISO(data.financialDateAdded);

        data.financialDateAdded = financialDataAddedFormatted;

        const updatedData = {
            ...data,
            receipt: data.receipt || (originalFinancial && originalFinancial.receipt) || null,
            valor: toCents(data.valor)
        };

        setFinancialData(updatedData, id)
            .then((response: any) => {
                showAlert('Dados financeiros editados com sucesso', SEVERITY.SUCCESS);
                navigate(`/projeto/${response.idProject}`, { replace: true });
            })
            .catch((error: any) => {
                setIsLoading(false);
                showAlert('Erro ao atualizar dados financeiros', SEVERITY.ERROR);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    
    // Função para lidar com a exclusão de dados financeiros
    const handleDeleteFinancial = async () => {
        if (!id) return;

        setIsLoading(true);
        try {
            await deleteFinancialService(id);
            showAlert('Registro financeiro excluído com sucesso', SEVERITY.SUCCESS);
            
            // Navega para o projeto após a exclusão
            if (projectId) {
                navigate(`/projeto/${projectId}`, { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (error) {
            console.error('Erro ao excluir registro financeiro:', error);
            showAlert('Erro ao excluir registro financeiro', SEVERITY.ERROR);
            setIsLoading(false);
        } finally {
            deleteModal.closeModal();
        }
    };
    
    return (
        <div className="container-edit-financial">
            <MenuTop />
            <div className="container-edit-financial__container">
                <Breadcrumb 
                    links={['Projetos', 'Editar movimentação financeira']} 
                    href={[ `/projeto/${projectId}`, '']} 
                    onNavigate={navigate}
                />
                    
                <InfoComponent />
                <Form
                    data={formData}
                    title="Editar movimentação financeira"
                    onSubmitForm={onSubmitForm}
                    onCancel={() => navigate(`/projeto-list/${projectId}`, { replace: true })}
                    isLoading={isLoading}
                    showDeleteIcon={true}
                    onClickedMenuContextType={(type: string) => {
                        if (type === 'delete') {
                            deleteModal.openModal();
                        }
                    }}
                />
                
                {/* Modal de confirmação de exclusão */}
                <CustomModal
                    open={deleteModal.isOpen}
                    onClose={deleteModal.closeModal}
                    title="Confirmar exclusão"
                    content={
                        <Typography>
                            Tem certeza que deseja excluir este registro financeiro? Esta ação não pode ser desfeita.
                        </Typography>
                    }
                    buttons={[
                        {
                            id: 'cancel',
                            label: 'Cancelar',
                            variant: 'outlined',
                            color: 'primary',
                            onClick: deleteModal.closeModal
                        },
                        {
                            id: 'confirm',
                            label: 'Excluir',
                            variant: 'contained',
                            color: 'error',
                            onClick: handleDeleteFinancial
                        }
                    ]}
                />
            </div>
        </div>
    );
}

export default FinancialEdit;
